import { ReloadOutlined } from '@ant-design/icons';
import { Button, Space, Tag, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import ExternalLink from '../components/external-link';
import Link from '../components/link';
import StatusTag from '../components/status-tag';
import Table from '../components/table';
import UpdatedAt from '../components/updated-at';
import { EDateFormat } from '../fragments/date-format';
import { getDateRangeColumnFilter } from '../fragments/date-range-column-filter';
import { getPacketTrackingUrl } from '../fragments/packet-tracking-url';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { packetsLoadAll } from '../reducers/packets/reducer';
import { type IAdminCarrierPacket } from '../reducers/packets/types';
import { SPACING } from '../theme/tokens';

const { Text } = Typography;

interface DataType {
  key: number;
  id: number;
  createdAt: string;
  thirdPartyId: string;
  barcode: string | null;
  vendor: string;
  vendorName: string;
  carrierService: string;
  orderUuid: string;
  orderId: number;
  latestStatus: string | null;
  billedPrice: number | null;
  billedCurrency: string | null;
  billedConverted: number | null;
  orderDeliveryFeeWithoutTax: number | null;
  orderCurrencySymbol: string | null;
  priceDiff: number | null;
}

function PacketsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const packetsPackets = useSelector((s: RootState) => s.Packets.packets);
  const packetsInProcess = useSelector((s: RootState) => s.Packets.inProcess);
  const packetsUpdatedAt = useSelector((s: RootState) => s.Packets.updatedAt);

  useEffect((): void => {
    if (!packetsPackets) {
      dispatch(packetsLoadAll());
    }
  }, [dispatch]);

  const dataSource = useMemo<DataType[]>(
    () =>
      (packetsPackets ?? [])
        .map((packet: IAdminCarrierPacket): DataType => {
          const statuses = [...(packet.CarrierPacketStatuses ?? [])].sort(
            (a, b) => a.id - b.id,
          );
          const latestStatus =
            statuses.length > 0 ? statuses[statuses.length - 1].status : null;
          const billedConverted = packet.billedShippingPriceConverted ?? null;
          const orderDeliveryFeeWithoutTax =
            packet.Order.deliveryFeeWithoutTax ?? null;
          const priceDiff =
            billedConverted !== null && orderDeliveryFeeWithoutTax !== null
              ? billedConverted - orderDeliveryFeeWithoutTax
              : null;

          return {
            key: packet.id,
            id: packet.id,
            createdAt: packet.createdAt,
            thirdPartyId: packet.thirdPartyId,
            barcode: packet.barcode,
            vendor: packet.CarrierService.CarrierVendorType.code,
            vendorName: packet.CarrierService.CarrierVendorType.name,
            carrierService: packet.CarrierService.name,
            orderUuid: packet.Order.uuid,
            orderId: packet.Order.id,
            latestStatus,
            billedPrice: packet.billedShippingPrice ?? null,
            billedCurrency: packet.billedShippingCurrency ?? null,
            billedConverted,
            orderDeliveryFeeWithoutTax,
            orderCurrencySymbol: packet.Order.CurrencyType?.symbol ?? null,
            priceDiff,
          };
        })
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
    [packetsPackets],
  );

  const columns = useMemo<ColumnsType>(() => {
    const carrierServiceFilters = [
      ...new Set(dataSource.map((r) => r.carrierService)),
    ]
      .sort()
      .map((name) => ({ text: name, value: name }));

    const vendorFilters = [
      ...new Map(dataSource.map((r) => [r.vendor, r.vendorName])).entries(),
    ].map(([code, name]) => ({ text: name, value: code }));

    return [
      {
        title: 'Created',
        dataIndex: 'createdAt',
        render: (val: string): React.ReactElement => (
          <Text>{dayjs(val).format(EDateFormat.DATE)}</Text>
        ),
        sorter: (a: DataType, b: DataType): number =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        defaultSortOrder: 'descend',
        ...getDateRangeColumnFilter<DataType>(
          (record: DataType): string => record.createdAt,
        ),
        width: '7%',
      },
      {
        title: 'Tracking No.',
        dataIndex: 'thirdPartyId',
        sorter: (a: DataType, b: DataType): number =>
          a.thirdPartyId.localeCompare(b.thirdPartyId),
        render: (text: string, record: DataType): React.ReactElement => (
          <Link to={`/packet/${record.id}`} label={text} openInNewTab />
        ),
        width: '16%',
        ...getColumnSearch('thirdPartyId'),
      },
      {
        title: 'Barcode',
        dataIndex: 'barcode',
        render: (
          _barcode: string | null,
          record: DataType,
        ): React.ReactElement => {
          if (!record.barcode) return <Text>---</Text>;
          const trackingUrl = getPacketTrackingUrl(
            record.barcode,
            record.vendor,
          );
          if (trackingUrl) {
            return (
              <ExternalLink
                to={trackingUrl}
                label={record.barcode}
                ariaLabel="Open tracking in new tab"
              />
            );
          }
          return <Text>{record.barcode}</Text>;
        },
        width: '12%',
        ...getColumnSearch('barcode'),
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
        render: (_vendor: string, record: DataType): React.ReactElement => (
          <Tag>{record.vendorName}</Tag>
        ),
        filters: vendorFilters,
        onFilter: (value, record: DataType): boolean => record.vendor === value,
        width: '8%',
      },
      {
        title: 'Carrier Service',
        dataIndex: 'carrierService',
        sorter: (a: DataType, b: DataType): number =>
          a.carrierService.localeCompare(b.carrierService),
        filters: carrierServiceFilters,
        onFilter: (value, record: DataType): boolean =>
          record.carrierService === value,
        width: '13%',
      },
      {
        title: 'Order',
        dataIndex: 'orderUuid',
        render: (_uuid: string, record: DataType): React.ReactElement => (
          <Link
            to={`/order/${record.orderUuid}`}
            label={record.orderUuid}
            openInNewTab
          />
        ),
        width: '22%',
      },
      {
        title: 'Latest Status',
        dataIndex: 'latestStatus',
        render: (status: string | null): React.ReactElement => (
          <StatusTag status={status ?? 'UNKNOWN'} />
        ),
        filters: [
          { text: 'Delivered', value: 'DELIVERED' },
          { text: 'In Transit', value: 'IN_TRANSIT' },
          { text: 'Returned', value: 'RETURNED' },
          { text: 'Unknown', value: '' },
        ],
        onFilter: (value, record: DataType): boolean => {
          if (value === '') return record.latestStatus === null;
          return record.latestStatus === value;
        },
        width: '10%',
      },
      {
        title: 'Billed',
        dataIndex: 'billedConverted',
        render: (_: unknown, record: DataType): React.ReactElement => {
          if (record.billedConverted === null) {
            return <Text type="secondary">---</Text>;
          }
          const showOriginal =
            record.billedPrice !== null &&
            record.billedCurrency !== null &&
            record.billedCurrency !== record.orderCurrencySymbol;
          return (
            <Space size={SPACING.xs} orientation="vertical">
              <Text>
                {record.billedConverted.toFixed(2)} {record.orderCurrencySymbol}
              </Text>
              {showOriginal && (
                <Text type="secondary">
                  {record.billedPrice} {record.billedCurrency}
                </Text>
              )}
            </Space>
          );
        },
        width: '10%',
      },
      {
        title: 'Diff',
        dataIndex: 'priceDiff',
        render: (_: unknown, record: DataType): React.ReactElement => {
          if (record.priceDiff === null) {
            return <Text type="secondary">---</Text>;
          }
          const sign = record.priceDiff >= 0 ? '+' : '';
          return (
            <Text type={record.priceDiff > 0 ? 'danger' : 'success'}>
              {sign}
              {record.priceDiff.toFixed(2)} {record.orderCurrencySymbol}
            </Text>
          );
        },
        sorter: (a: DataType, b: DataType): number =>
          (a.priceDiff ?? 0) - (b.priceDiff ?? 0),
        filters: [
          { text: 'Positive (overpaid)', value: 'positive' },
          { text: 'Negative (underpaid)', value: 'negative' },
          { text: 'Zero (exact)', value: 'zero' },
          { text: 'Not billed', value: 'null' },
        ],
        onFilter: (value, record: DataType): boolean => {
          if (value === 'null') return record.priceDiff === null;
          if (value === 'zero') return record.priceDiff === 0;
          if (value === 'positive')
            return record.priceDiff !== null && record.priceDiff > 0;
          if (value === 'negative')
            return record.priceDiff !== null && record.priceDiff < 0;
          return true;
        },
        width: '10%',
      },
    ];
  }, [dataSource]);

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <Space size={SPACING.lg} align="center">
        <Button
          type={!packetsPackets ? 'primary' : 'default'}
          onClick={(): void => {
            dispatch(packetsLoadAll());
          }}
          disabled={packetsInProcess}
        >
          Reload all <ReloadOutlined />
        </Button>

        <UpdatedAt value={packetsUpdatedAt} />
      </Space>

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={packetsInProcess && !packetsPackets}
        emptyText="No shipments found"
      />
    </Space>
  );
}

export default PacketsContainer;
